type StoryBibleRecord = {
  summary?: string | null;
  markdown?: string | null;
  structuredJson?: string | null;
} | null | undefined;

type QuickFact = { key?: string; value?: string };

const MAX_LIST_ITEMS = 14;
const MAX_LINE_LENGTH = 320;

const trimText = (value: unknown) => (typeof value === "string" ? value.trim() : "");

const clip = (value: string, limit = MAX_LINE_LENGTH) =>
  value.length > limit ? `${value.slice(0, limit - 1).trimEnd()}…` : value;

const safeArray = <T = any>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const parseStructured = (raw?: string | null) => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const formatQuickFacts = (facts: unknown) =>
  safeArray<QuickFact>(facts)
    .map((fact) => {
      const key = trimText(fact?.key);
      const value = trimText(fact?.value);
      if (!key || !value) return "";
      return `${key}: ${value}`;
    })
    .filter(Boolean);

const formatEntryLine = (title: string, details: unknown[]) => {
  const cleanedDetails = details
    .map((detail) => trimText(detail))
    .filter(Boolean);

  if (cleanedDetails.length === 0) {
    return `- ${clip(title)}`;
  }

  return `- ${clip(`${title}: ${cleanedDetails.join(" | ")}`)}`;
};

const pushSection = (lines: string[], title: string, values: string[]) => {
  const cleaned = values.filter(Boolean).slice(0, MAX_LIST_ITEMS);
  if (cleaned.length === 0) return;
  lines.push(`${title}:`);
  lines.push(...cleaned);
  lines.push("");
};

const buildCharacterLines = (characters: unknown) =>
  safeArray<any>(characters).map((character) => {
    const name = trimText(character?.name) || "Unnamed character";
    const details = [
      trimText(character?.arcState) ? `Current state: ${trimText(character.arcState)}` : "",
      trimText(character?.personality) ? `Personality: ${trimText(character.personality)}` : "",
      trimText(character?.goals) ? `Goals: ${trimText(character.goals)}` : "",
      trimText(character?.conflicts) ? `Conflicts: ${trimText(character.conflicts)}` : "",
      trimText(character?.backstory) ? `Backstory: ${trimText(character.backstory)}` : "",
      ...formatQuickFacts(character?.quickFacts),
      ...safeArray<string>(character?.relationships)
        .map((relationship) => trimText(relationship))
        .filter(Boolean)
        .slice(0, 3)
        .map((relationship) => `Relationship: ${relationship}`),
      ...safeArray<string>(character?.aliases)
        .map((alias) => trimText(alias))
        .filter(Boolean)
        .slice(0, 3)
        .map((alias) => `Alias: ${alias}`),
    ];
    return formatEntryLine(name, details);
  });

const buildCatalogLines = (label: string, values: unknown) =>
  safeArray<any>(values).map((item) => {
    const name = trimText(item?.name) || `Unnamed ${label.toLowerCase()}`;
    const details = [
      trimText(item?.description),
      ...formatQuickFacts(item?.quickFacts),
      ...safeArray<string>(item?.aliases)
        .map((alias) => trimText(alias))
        .filter(Boolean)
        .slice(0, 3)
        .map((alias) => `Alias: ${alias}`),
      ...safeArray<string>(item?.tags)
        .map((tag) => trimText(tag))
        .filter(Boolean)
        .slice(0, 3)
        .map((tag) => `Tag: ${tag}`),
    ];
    return formatEntryLine(name, details);
  });

const buildRelationshipLines = (relationships: unknown) =>
  safeArray<any>(relationships).map((relationship) => {
    const pair = trimText(relationship?.pair) || "Unnamed relationship";
    const details = [
      trimText(relationship?.dynamic),
      trimText(relationship?.evolution)
        ? `Evolution: ${trimText(relationship.evolution)}`
        : "",
    ];
    return formatEntryLine(pair, details);
  });

const buildSimpleLines = (values: unknown) =>
  safeArray(values).map((value) => `- ${clip(trimText(value))}`).filter((value) => value !== "- ");

const buildSectionLines = (sections: unknown) =>
  safeArray<any>(sections).flatMap((section) => {
    const title = trimText(section?.title) || trimText(section?.key) || "Section";
    const lines: string[] = [];
    const sectionLines = [
      ...safeArray<string>(section?.paragraphs).map((paragraph) => `- ${clip(trimText(paragraph))}`),
      ...safeArray<string>(section?.bullets).map((bullet) => `- ${clip(trimText(bullet))}`),
      ...safeArray<any>(section?.entries).map((entry) => {
        const entryTitle = trimText(entry?.title) || "Entry";
        const details = [
          ...safeArray<string>(entry?.paragraphs),
          ...safeArray<string>(entry?.bullets),
        ];
        return formatEntryLine(entryTitle, details);
      }),
    ].filter((value) => value !== "- ");

    if (sectionLines.length === 0) return [];
    lines.push(`${title}:`);
    lines.push(...sectionLines.slice(0, MAX_LIST_ITEMS));
    lines.push("");
    return lines;
  });

export const buildStoryBiblePromptContext = (storyBible: StoryBibleRecord) => {
  if (!storyBible) return "";

  const structured = parseStructured(storyBible.structuredJson);
  const lines: string[] = [];
  const summary = trimText(storyBible.summary);

  if (summary) {
    lines.push(`Summary: ${clip(summary, 500)}`);
    lines.push("");
  }

  if (!structured) {
    return lines.join("\n").trim();
  }

  pushSection(lines, "Characters", buildCharacterLines((structured as any).characters));
  pushSection(lines, "Relationships", buildRelationshipLines((structured as any).relationships));
  pushSection(lines, "Locations", buildCatalogLines("Location", (structured as any).locations));
  pushSection(lines, "Items", buildCatalogLines("Item", (structured as any).items));
  pushSection(lines, "Lore & World Rules", [
    ...buildSimpleLines((structured as any).worldRules),
    ...buildCatalogLines("Lore", (structured as any).lore),
  ]);
  pushSection(lines, "Plot Points", buildSimpleLines((structured as any).plotPoints));
  pushSection(lines, "Timeline", buildSimpleLines((structured as any).timeline));
  pushSection(lines, "Open Threads", [
    ...buildSimpleLines((structured as any).openThreads),
    ...buildCatalogLines("Open Thread", (structured as any).custom),
  ]);
  pushSection(lines, "Revision Risks", buildSimpleLines((structured as any).revisionRisks));

  const fallbackSections = buildSectionLines((structured as any).sections);
  if (lines.length <= 2 && fallbackSections.length > 0) {
    lines.push(...fallbackSections);
  }

  return lines.join("\n").trim();
};
